/**
 * CV SCREEN — what happens when a candidate's CV arrives on a requirement (6 Oct 2026).
 *
 * The import → CV → interview flow: a recruiter imports a list, each person is emailed the
 * "complete your application" link, and when their CV comes back it can be scored against the
 * requirement straight away. What the score then does is the team's choice, per requirement:
 *
 *   off      nothing automatic — the CV waits for a recruiter to run matching (today's behaviour,
 *            and the default: scoring costs a resume-scan credit, so it is never switched on silently)
 *   suggest  score on arrival; at/above the threshold the person is queued as "ready to invite"
 *            and a recruiter approves the interview (one click, billed then)
 *   auto     score on arrival; at/above the threshold the interview link is emailed at once
 *
 * HONESTY CONTRACTS (docs/GROUND_RULES.md). Below the threshold NOTHING happens to the person — no
 * rejection, no email; a human decides. A degraded score (provider failure) is never a pass: it is
 * left for review and not billed. The threshold only ever triggers an invite, never a cut.
 */
import { z } from "zod";
export const CV_SCREEN_MODES = ["off", "suggest", "auto"];
export const CV_SCREEN_DEFAULT_THRESHOLD = 60;
export const cvScreenSchema = z.object({
    mode: z.enum(CV_SCREEN_MODES),
    /** Resume match score (0-100) at/above which a CV passes. */
    threshold: z.number().int().min(1).max(100).default(CV_SCREEN_DEFAULT_THRESHOLD),
});
/** The setting in force for a requirement — absent / malformed = off. */
export function cvScreenOf(config) {
    const parsed = cvScreenSchema.safeParse(config?.cvScreen);
    return parsed.success ? parsed.data : { mode: "off", threshold: CV_SCREEN_DEFAULT_THRESHOLD };
}
/** Did a score clear the bar? A null (degraded / not scored) score never passes. */
export function cvPasses(score, screen) {
    return typeof score === "number" && Number.isFinite(score) && score >= screen.threshold;
}
/**
 * Where one imported / CV-requested person stands, for the import status view. Ordered: each stage
 * implies the ones before it. `below` and `review` are side branches of `scored`, not steps.
 */
export const IMPORT_STAGES = [
    "requested",
    "opened",
    "submitted",
    "scored",
    "passed",
    "invited",
    "interviewed",
];
//# sourceMappingURL=cvScreen.js.map